import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { O2dTab } from '../interfaces/tab.interface.js';
import { firstEnabled, lastEnabled, nextEnabled, prevEnabled } from './enabled-navigation.js';

/**
 * Host adapter the `TabKeyboardController` drives. The host is the Lit element
 * (`ReactiveControllerHost & EventTarget`) that owns the tab config and the
 * currently effective selection, and applies a keyboard-requested selection via
 * `selectTab` (which, for `<o2d-tab-list>`, updates local state and emits the
 * public `selected` event — selection-follows-focus).
 */
export interface TabKeyboardHost extends ReactiveControllerHost, EventTarget {
    readonly tabs: readonly O2dTab[];
    readonly effectiveActiveId: string | undefined;
    selectTab(id: string): void;
}

/** Resolves the navigation-key target: the enabled tab id to select, or none. */
type NavIntentResolver = (host: TabKeyboardHost) => string | undefined;

/**
 * Maps a keyboard `key` to the enabled tab it should move selection to, using
 * the pure Phase-2 navigation helpers. Arrow keys step relative to the effective
 * active id (wrapping, skipping disabled); Home/End jump to the first/last
 * enabled tab. A missing effective active id (all-disabled / empty) yields no
 * target, so no selection change is requested. Keys absent from this map are
 * non-navigation keys.
 */
const NAV_KEY_RESOLVERS: Record<string, NavIntentResolver> = {
    ArrowRight: (host) => stepFromActive(host, nextEnabled),
    ArrowLeft: (host) => stepFromActive(host, prevEnabled),
    Home: (host) => firstEnabled(host.tabs),
    End: (host) => lastEnabled(host.tabs),
};

/** Applies a relative mover from the effective active id, or nothing when unset. */
function stepFromActive(
    host: TabKeyboardHost,
    mover: (tabs: readonly O2dTab[], currentId: string) => string | undefined,
): string | undefined {
    const current = host.effectiveActiveId;
    return current === undefined ? undefined : mover(host.tabs, current);
}

/**
 * Plain reactive controller translating tablist keydown events into selection
 * requests (WAI-ARIA roving tabindex, automatic activation). It only computes
 * the target id and calls back into the host; the host owns render + focus. No
 * RxJS — a bound listener attached in the constructor and removed on disconnect.
 */
export class TabKeyboardController implements ReactiveController {
    private _host: TabKeyboardHost;

    constructor(host: TabKeyboardHost) {
        this._host = host;
        host.addController(this);

        this._handleKeydown = this._handleKeydown.bind(this);
        host.addEventListener('keydown', this._handleKeydown as EventListener);
    }

    /** Required by ReactiveController. Listener already attached in constructor. */
    hostConnected(): void {
        // No-op — setup done in constructor.
    }

    /** Tears down the keydown listener when the host leaves the DOM. */
    hostDisconnected(): void {
        this._host.removeEventListener('keydown', this._handleKeydown as EventListener);
    }

    /**
     * Resolves the navigation key to a target tab and requests its selection.
     * Non-navigation keys are ignored (no `preventDefault`); navigation keys
     * always suppress the default page scroll, and request a selection only when
     * an enabled target exists.
     */
    private _handleKeydown(event: KeyboardEvent): void {
        const resolveTarget = this._navIntentFor(event.key);
        if (!resolveTarget) {
            return;
        }
        event.preventDefault();
        const targetId = resolveTarget(this._host);
        if (targetId !== undefined) {
            this._host.selectTab(targetId);
        }
    }

    /**
     * Key -> navigation-intent mapping: returns the resolver for a navigation key
     * (Arrow/Home/End), or `undefined` for any non-navigation key.
     */
    private _navIntentFor(key: string): NavIntentResolver | undefined {
        return NAV_KEY_RESOLVERS[key];
    }
}
